// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, Component, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateComponentStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateComponentStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateComponentStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogComponent = searchParams.get('dialogComponent');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [component, setComponent] = useState<Component | null>(null);

	// fetch component
	const { isLoading: isLoadingComponent } = useFetch({
		isEnabled: Boolean(paramDialogComponent && isOpen),
		url: `${process.env.REACT_APP_API_URL}/components/${paramDialogComponent}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponent(res.data[0]);
			}
		},
	});

	// vars
	const dialogActionProps: Partial<DialogActionsProps> = {};
	const isActivating = paramDialogStatus === 'ACTIVE';

	// deactivate translations
	const dialog = {
		alert: t('alert.deactivateComponent'),
		btnSubmit: {
			children: t('deactivateComponent'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateComponentStatus.deactivate.content',
		title: t('deactivateComponent'),
	};

	// activate translations
	if (isActivating) {
		dialog.btnSubmit = {
			children: t('activateComponent'),
			color: 'primary',
		};
		dialog.alert = t('alert.activateComponent');
		dialog.i18nKey = 'dialogUpdateComponentStatus.activate.content';
		dialog.title = t('activateComponent');
	}

	if (component) {
		dialogActionProps.subtitle = {
			children: component.name,
		};
	}

	// patch component
	const { fetchRequest: updateComponent, isLoading: isLoadingUpdateComponent } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/components/${paramDialogComponent}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				status: paramDialogStatus,
			}),
		},
		onError: (feedback) => {
			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (feedback.id === 'COMPONENT_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('feedback.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			let message = t('alert.componentDeactivated', { name: component?.name });

			if (isActivating) {
				message = t('alert.componentActivated', { name: component?.name });
			}

			// set banner
			setBanner({
				hasClose: true,
				message,
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// update component
		updateComponent();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingComponent) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: component?.name }}
					/>
				</Grid>
				{dialog.alert && (
					<Grid item xs={12}>
						<Alert severity="warning">{dialog.alert}</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateComponent,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'component',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: dialog.title,
			}}
			{...dialogActionProps}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
